import { Component, Input, OnInit, OnDestroy, Inject, Renderer2, HostBinding } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { asideMenuCssClasses } from '../shared';
import { AsideBarService } from 'src/modules/base/services/asideBar.service';
import { CommonService } from 'src/modules/base/services/common.service';
import { TranslateService } from 'src/modules/base/services/translate.service';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { PreferenceService } from 'src/modules/base/shared/services/preference.service';
import { UtilService } from 'src/modules/base/services/util.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-aside, cui-aside',
    templateUrl: './app-aside.component.html',
    standalone: false
})
export class AppAsideComponent implements OnInit, OnDestroy {
  @Input() display: any;
  @Input() fixed: boolean;
  @Input() offCanvas: boolean;
  header: string = "";

  private readonly fixedClass = 'aside-menu-fixed';

  @HostBinding('class.aside-menu') _aside = true;
  toggler: any;
  config: any;
  showResizeAerrow: boolean = false;
  subs: Subscription = new Subscription();
  private resizeCleanupListeners: Array<() => void> = [];
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private readonly asideMinWidth = 360;
  private readonly asideMaxWidthRatio = 0.85;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2,
    public commonService: AsideBarService,
    public common: CommonService,
    public utilService: UtilService,
    public translateService: TranslateService,
    private baseService: BaseService,
    private preferenceService: PreferenceService,
  ) { }

  ngOnInit(): void {
    this.isFixed(this.fixed);
    this.isOffCanvas(this.offCanvas);
    this.displayBreakpoint(this.display);
    this.setHeader();
    this.setToggleButton();
    this.setResizeAerrow();
    this.expandForm();
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, this.fixedClass);
    this.stopResize();
    this.resetResizableAside();
    this.subs.unsubscribe();
  }

  isFixed(fixed: boolean = this.fixed): void {
    if (fixed) {
      this.renderer.addClass(this.document.body, this.fixedClass);
    }
  }

  isOffCanvas(offCanvas: boolean = this.offCanvas): void {
    if (offCanvas) {
      this.renderer.addClass(this.document.body, 'aside-menu-off-canvas');
    }
  }

  displayBreakpoint(display: any = this.display): void {
    if (display !== false) {
      const cssClass = this.display ? `aside-menu-${this.display}-show` : asideMenuCssClasses[0];
      this.renderer.addClass(this.document.body, cssClass);
    }
  }

  setHeader() {
    this.subs.add(this.commonService.setHeader().subscribe(header => {
      this.header = header;
      this.subs.add(this.translateService.translate(header).subscribe(trans_key => {
        if (trans_key) {
          this.header = trans_key;
        }
      }));
    }));
  }

  setToggleButton(){
    this.subs.add(this.commonService.setToggler().subscribe(toggler => {
        this.toggler = toggler;
    }));
  }

  setResizeAerrow(){
    this.subs.add(this.commonService.setResizeAerrow().subscribe(showResizeAerrow => {
      this.showResizeAerrow = showResizeAerrow === true;
      if (!this.showResizeAerrow) {
        this.resetResizableAside();
      }
    }));
  }

  expandForm(){
    this.subs.add(this.commonService.setExpandBtn().subscribe(expand => {
      let doc = this.document.getElementById('aside-modal-container');
      if(expand){
        this.config = expand;
        if(this.config.preference && this.config.preference.expand){
          doc.classList.add('expand');
        }else if(this.config.preference && this.config.preference.collapse){
          doc.classList.remove('expand');
        }else if(expand.expand){
          doc.classList.add('expand');
        }else if(expand.collapse){
          doc.classList.remove('expand');
        }else{
          doc.classList.remove('expand');
        }
      }else{
        doc.classList.remove('expand');
      }
    }));
  }

  multiselectScreen(e){
    this.commonService.expandCollapse(e);
  }

  toggleOn(){
    if(this.toggler.multiselect == true){
      this.toggler.multiselect = false;
    }else{
      this.toggler.multiselect = true;
    }
    this.commonService.btnToggler(this.toggler);
  }

  closeAsideBar(){
    console.log("form Service ", this.common['_manualChange']);
    if(this.common['_manualChange']){
        this.commonService.checkAndSaveDraft((skipAnotherPopup) => {
            if (skipAnotherPopup) {
              this.closeAndCleanup();
            } else {
              const dialogRef = this.baseService.modalService.confirm('LEAVE', 'Are you sure you want to leave the page?');
              this.subs.add(dialogRef.subscribe(result => {
                  if (result) {
                    this.closeAndCleanup();
                  }
              }));
            }
        });
    } else {
        this.closeAndCleanup();
    }
  }

  private closeAndCleanup() {
    this.commonService.removeComponent();
    this.commonService.classToggler('cstmOverlay');
    this.commonService.closeBtnClicked();
    let doc = this.document.getElementById('aside-modal-container');
    doc.classList.remove('expand');
    this.resetResizableAside();
  }

  startResize(event: MouseEvent) {
    if (!this.showResizeAerrow || this.utilService.isMobile()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const asidebarContainer = this.document.getElementById('aside-modal-container');
    if (!asidebarContainer) {
      return;
    }

    this.resizeStartX = event.clientX;
    this.resizeStartWidth = asidebarContainer.getBoundingClientRect().width;
    asidebarContainer.classList.add('aside-resizing');
    this.document.body.classList.add('aside-resizing', 'aside-resized');

    this.resizeCleanupListeners = [
      this.renderer.listen('document', 'mousemove', (moveEvent: MouseEvent) => this.resizeAside(moveEvent)),
      this.renderer.listen('document', 'mouseup', () => this.stopResize())
    ];
  }

  private resizeAside(event: MouseEvent) {
    const viewportWidth = window.innerWidth || this.document.documentElement.clientWidth;
    const maxWidth = Math.floor(viewportWidth * this.asideMaxWidthRatio);
    const minWidth = Math.min(this.asideMinWidth, Math.floor(viewportWidth * 0.9));
    const draggedWidth = this.resizeStartWidth + (this.resizeStartX - event.clientX);
    const nextWidth = Math.max(minWidth, Math.min(maxWidth, draggedWidth));

    this.document.documentElement.style.setProperty('--aside-resize-width', `${nextWidth}px`);
  }

  private stopResize() {
    this.resizeCleanupListeners.forEach(removeListener => removeListener());
    this.resizeCleanupListeners = [];

    const asidebarContainer = this.document.getElementById('aside-modal-container');
    if (asidebarContainer) {
      asidebarContainer.classList.remove('aside-resizing');
    }
    this.document.body.classList.remove('aside-resizing');
  }

  private resetResizableAside() {
    this.stopResize();
    this.document.body.classList.remove('aside-resized');
    this.document.documentElement.style.removeProperty('--aside-resize-width');
  }

  expandAsidebar(event){
    if(this.config && this.config.preferenceKey && (this.config.preference == undefined || Object.keys(this.config.preference).length <= 0)){
      const dialogRef = this.baseService.modalService.confirmCustomBtn('Expand/Collapse', 'Do you want to expand/collapse form every time ?', '', { cancel: "Collapse", ok: "Expand", cancelFunction: "hide" }, true);
      this.subs.add(dialogRef.subscribe(result => {
        if(result == null){}
        else if (result) {
          this.config.preference.expand = true;
          this.config.preference.collapse = false;
          this.preferenceService.saveTableConfigData(this.config.preferenceKey, this.config.preference);
          this.expandData(event);
        } else {
          this.config.preference.collapse = true;
          this.config.preference.expand = false;
          this.preferenceService.saveTableConfigData(this.config.preferenceKey, this.config.preference);
          this.expandData(event);
        }
      }, () => { }));
    }else{
      this.expandData(event);
    }
  }

  expandData(event){
    this.resetResizableAside();
    const asidebarContainerId = this.document.getElementById('aside-modal-container'); 
    // console.log(asidebarContainer);
    // console.log(asideMenuCssClasses);
    if(asidebarContainerId && asidebarContainerId != null){
      console.log('correct');
      if(!this.commonService.expandButton){
        this.setCollapseClasses()
      }
      //need to toggle class on asidebar
      if(asidebarContainerId.classList.contains('expand')){
        asidebarContainerId.classList.add("modal-aside-menu-lg");
        asidebarContainerId.classList.remove('modal-aside-menu-xl');
        document.body.classList.add("modal-aside-menu-lg");
        document.body.classList.remove("modal-aside-menu-xl");
        document.body.classList.remove("modal-aside-menu-md");
        document.body.classList.remove("modal-aside-menu-sm");
      }else{
        asidebarContainerId.classList.remove("modal-aside-menu-lg");
        asidebarContainerId.classList.add('modal-aside-menu-xl');
        document.body.classList.add("modal-aside-menu-xl");
        document.body.classList.remove("modal-aside-menu-lg");
        document.body.classList.remove("modal-aside-menu-md");
        document.body.classList.remove("modal-aside-menu-sm");
      }
      asidebarContainerId.classList.toggle('expand');
    }
    else{
      console.log('not correct');
    }
  }

  setCollapseClasses(){
    this.commonService.expandButton = true
    let doc = document.getElementById('bar-right');
    if (doc) {
    doc.style.height = '70px';
    }
    let sticky = document.getElementsByClassName('sticky-add-btn');
    let form_class;
    if(sticky && sticky.length > 0){
      sticky[0]['style'].bottom = '80px';
    }
    if(document.getElementById('footer-tab-style')){
      document.getElementById('footer-tab-style').remove();
    }
    if(document.getElementById('aside_ui_engine')){
      form_class = document.getElementsByClassName('aside_ui_engine');
    }
    if(form_class && form_class.length > 0){
      form_class[0]['style'].height = "calc(100% - 190px) !important";
    }
  }

}
